import { Header } from "components/atoms/Typography";
import { IncreaseDecreasePercentage } from "components/molecules/Card/CardFunctions";
import ChartContainer from "components/molecules/ChartContainer";
import { AreaChart } from "components/molecules/Charts";
import { AreaChartDatum } from "components/molecules/Charts/AreaChart";
import { ChartNoDataState } from "components/molecules/NoDataState";
import { DATA_KEY_COLORS } from "constants/chartsColors.constant";
import { useMemo } from "react";
import usePlansState from "recoilStore/Building/usePlansState";
import { prepareEnergyOutcomesChart } from "./EnergyCard.helper";

interface EnergyOutcomesChartProps {
    backgroundColor?: string;
}

function EnergyOutcomesChart({ backgroundColor }: EnergyOutcomesChartProps) {
    const { referencePlan, comparedPlan, referencePlanName, comparedPlanName, referencedPlanId, comparedPlanId } =
        usePlansState();
    const {
        chartEnergyData,
        energyDifference,
        tooltipElement,
        formatedEnergyRef,
        refEnergyUnit,
        energyFormatFunction,
    } = prepareEnergyOutcomesChart(referencePlan, comparedPlan, referencePlanName, comparedPlanName);

    const dataWithCalculatedGoalLine = useMemo((): AreaChartDatum[] => {
        if (!chartEnergyData || chartEnergyData?.length === 0) return [];

        const baselineData: { x: number; y: number }[] = [];
        const recommendationsData: { x: number; y: number }[] = [];

        chartEnergyData.forEach(({ year: x, totalEnergyCompared, totalEnergyReference }) => {
            baselineData.push({
                x,
                y: totalEnergyCompared || 0,
            });
            recommendationsData.push({
                x,
                y: totalEnergyReference || 0,
            });
        });
        return [
            {
                type: "line" as const,
                id: referencePlanName,
                color: DATA_KEY_COLORS.recommendations,
                data: recommendationsData,
                formatKey: "annualCarbonEmissionsCurrent",
            },
            ...(comparedPlanId !== referencedPlanId
                ? [
                      {
                          type: "line" as const,
                          id: comparedPlanName,
                          color: DATA_KEY_COLORS.baseline,
                          data: baselineData,
                          formatKey: "annualCarbonEmissionsCurrent",
                      },
                  ]
                : []),
        ];
    }, [chartEnergyData]);

    const maxYValue = energyFormatFunction(
        Math.max(...dataWithCalculatedGoalLine.flatMap((series) => series.data.map((d) => d.y)))
    );

    if (chartEnergyData === null) return <ChartNoDataState title="Annual Carbon Emissions" />;

    return (
        <ChartContainer title="Energy" exportableData={chartEnergyData}>
            <div className="card__value-and-percentage" style={{ justifyContent: "space-between", width: "70%" }}>
                <Header>
                    {/* <Header style={{ color: valueColor }}> */}
                    {formatedEnergyRef} {refEnergyUnit}
                </Header>
                <IncreaseDecreasePercentage isHeader={true} value={energyDifference} tooltipElement={tooltipElement} />
            </div>
            <AreaChart
                data={dataWithCalculatedGoalLine}
                yAxisInfo={{
                    formatFunction: (v: string | number) => {
                        const shouldShowDecimals = maxYValue < 10;
                        return energyFormatFunction(v, shouldShowDecimals);
                    },
                    unit: refEnergyUnit || "",
                }}
                backgroundColor={backgroundColor}
                enableGridX={true}
                height={350}
            />
        </ChartContainer>
    );
}

export default EnergyOutcomesChart;
